import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { useAuth, useCredentialValidation } from '../../auth';
import { AuthService } from '../../auth/services/auth.service';
import { RoleService } from '../services/role.service';
import { NavItem, AdminLayoutProps } from '../types/layout.types';
import { UserRole } from '../../auth/types/auth.types';
import { MetasColaboradorService } from '../../metas-colaborador/services/metasColaborador.service';
import type { TipoMiAlcance } from '../../metas-colaborador/types/metas.types';
import {
  almacenSesion,
  escucharAlcanceMetas,
  guardarMemoMenu,
  leerMemoMenu,
  MAX_REINTENTOS_ALCANCE,
  mostrarItemMetas,
  REINTENTO_ALCANCE_MS,
  requiereConsultarAlcance,
} from '../../metas-colaborador/utils/menu';
import { confirmarSalida } from '../../common/utils/cambiosSinGuardar';

const navItems: NavItem[] = [
  {
    path: '/users',
    label: 'Usuarios',
    icon: 'pi pi-users',
    // MANAGER salió del menú (Consulta CM, 30-oct): el gerente no puede entrar
    // al panel (ADMIN_APP_ROLES), así que el item era letra muerta.
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN]
  },
  {
    path: '/perfiles',
    label: 'Perfiles',
    icon: 'pi pi-sliders-h',
    // Qué MÓDULOS ve cada cuenta en Consulta CM (y en Metas del admin). Sólo TI.
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN]
  },
  {
    path: '/responsables',
    label: 'Responsables por tienda',
    icon: 'pi pi-sitemap',
    // Supervisor y regional de cada tienda, con historial. Los nombres son
    // llaves de acceso de Metas: sólo TI los reasigna.
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN]
  },
  {
    path: '/indicadores',
    label: 'Indicadores',
    icon: 'pi pi-chart-bar',
    // INDICADORES (2026-07-28): rol de solo lectura del tablero. Ve SOLO este
    // item del menú (no "Usuarios") — su seguridad real la da el backend (403).
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.INDICADORES]
  },
  {
    path: '/vendedores',
    label: 'Vendedores por tienda',
    icon: 'pi pi-id-card',
    // Ver lo que ve el vendedor al entrar, y mover gente entre tiendas por
    // temporada. BUK tarda semanas en reflejar un movimiento provisional: sin
    // esto el colaborador no aparece donde trabaja y sigue apareciendo donde
    // salio, con riesgo de que le generen solicitudes en la tienda equivocada.
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN]
  },
  {
    path: '/dispositivos',
    label: 'Dispositivos de tienda',
    icon: 'pi pi-mobile',
    // Cuántos equipos tiene una tienda, hacer sonar uno y cerrar la sesión de
    // uno perdido (spec 2026-10-13; caso TRE 74 Tezontle, 10-oct). El gerente
    // ve SU tienda (la API la fuerza) y no cierra sesiones; las cuentas
    // INDICADORES no la ven (decisión del usuario, 13-oct).
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.MANAGER]
  },
  {
    path: '/metas',
    label: 'Metas por colaborador',
    icon: 'pi pi-flag',
    // Los supervisores y regionales son cuentas INDICADORES: el rol les abre
    // la puerta, pero el item sólo aparece si tienen un ALCANCE de metas
    // asignado. Sin él verían una pantalla que dice «no tienes tiendas».
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.INDICADORES],
    requiereAlcanceMetas: true,
  },
  {
    path: '/erp',
    label: 'Salud del ERP',
    icon: 'pi pi-heart-fill',
    // Monitor del MySQL de Proscai. Existe por el incidente del 2026-08-15, en
    // el que un lote de reportes tumbo tienda + credito + reporteria y nos
    // enteramos 25 minutos despues, porque llamo una tienda.
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.INDICADORES]
  },
  /*{
    path: '/branch-offices',
    label: 'Sucursales',
    icon: 'pi pi-building',
    allowedRoles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.MANAGER]
  }*/
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [filteredNavItems, setFilteredNavItems] = useState<NavItem[]>([]);
  const [user, setUser] = useState('');
  // `undefined` = no se sabe todavía (o falló): el item de metas queda oculto.
  const [tipoAlcanceMetas, setTipoAlcanceMetas] = useState<TipoMiAlcance | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // Usar el hook de validación de credenciales
  useCredentialValidation();

  // Alcance de metas: sólo para INDICADORES (a los administradores se les
  // muestra sin preguntar). La respuesta se recuerda en sessionStorage por
  // correo y se vuelve a preguntar en cada cambio de ruta SI LA MEMORIA
  // CADUCÓ —antes se preguntaba una sola vez al montar el layout, y a quien le
  // asignaban tiendas con la pestaña abierta el item no le aparecía nunca—.
  // Una consulta a la vez: navegar rápido no dispara varias.
  const [reintento, setReintento] = useState(0);
  const enVuelo = useRef(false);
  const fallos = useRef(0);
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    const userRole = RoleService.getUserRole();
    if (!requiereConsultarAlcance(userRole)) return;
    const email = RoleService.getUserName() ?? '';
    const memo = leerMemoMenu(almacenSesion(), email, Date.now());
    if (memo !== undefined) {
      setTipoAlcanceMetas(memo);
      return;
    }
    if (enVuelo.current) return;
    enVuelo.current = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    MetasColaboradorService.getMiAlcance()
      .then((a) => {
        fallos.current = 0;
        guardarMemoMenu(almacenSesion(), email, a.tipo, Date.now());
        if (montado.current) setTipoAlcanceMetas(a.tipo);
      })
      .catch(() => {
        // Sin memoria guardada, el siguiente cambio de ruta vuelve a preguntar;
        // y por si la persona no navega, unos reintentos espaciados. El resto
        // del menú no depende de esto.
        fallos.current += 1;
        if (montado.current && fallos.current <= MAX_REINTENTOS_ALCANCE) {
          timer = setTimeout(() => {
            if (montado.current) setReintento((n) => n + 1);
          }, REINTENTO_ALCANCE_MS);
        }
      })
      .finally(() => {
        enVuelo.current = false;
      });
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname, reintento]);

  // La pantalla de metas avisa cada vez que lee `mi-alcance`: si a la cuenta le
  // acaban de asignar (o quitar) tiendas, el item aparece o desaparece sin
  // recargar ni esperar a que caduque la memoria.
  useEffect(
    () =>
      escucharAlcanceMetas((d) => {
        const email = (RoleService.getUserName() ?? '').trim().toLowerCase();
        if (d.email.trim().toLowerCase() === email) setTipoAlcanceMetas(d.tipo);
      }),
    [],
  );

  useEffect(() => {
    const userRole = RoleService.getUserRole();
    const user = RoleService.getUserName();
    if (user) setUser(user);
    
    const filtered = navItems.filter(item => {
      if (!item.allowedRoles || item.allowedRoles.length === 0) {
        return true;
      }
      if (!userRole || !item.allowedRoles.includes(userRole)) return false;
      return item.requiereAlcanceMetas ? mostrarItemMetas(userRole, tipoAlcanceMetas) : true;
    });
    
    setFilteredNavItems(filtered);
  }, [tipoAlcanceMetas]);

  // Salir con cambios sin guardar (la captura de metas) pregunta antes. La app
  // usa BrowserRouter, sin `useBlocker`: sin esto, «Cerrar sesión» o un clic en
  // el menú descartaban la captura en silencio.
  const handleLogout = () => {
    if (!confirmarSalida()) return;
    // Logout REAL de este equipo: la API revoca el refresh de este navegador.
    // Va antes de `logout()` (que borra el refresh) y no se espera: salir nunca
    // depende de la red.
    void AuthService.cerrarSesionEnServidor();
    logout();
    navigate('/auth/login', { replace: true });
  };

  const alNavegar = (e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => {
    // Ctrl/Cmd/Mayús o clic central abren OTRA pestaña: aquí no se sale de
    // nada, así que ni se pregunta ni se olvidan las marcas (si se olvidaban,
    // el siguiente clic normal salía sin preguntar con la captura a medias).
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    const mismaPantalla = location.pathname.startsWith(item.path);
    if (!mismaPantalla && !confirmarSalida()) {
      e.preventDefault();
      return;
    }
    setSidebarVisible(false);
  };

  // El título sale de TODOS los items, no de los filtrados: una cuenta sin el
  // item de metas (o mientras se consulta su alcance) que entra a /metas por
  // enlace veía «Dashboard» en la cabecera.
  const currentNavItem = navItems.find(item =>
    location.pathname.startsWith(item.path)
  );
  
  const pageTitle = currentNavItem?.label || 'Dashboard';

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-semibold text-gray-800">Dashboard</h2>
        </div>
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-rounded"
          onClick={() => setSidebarVisible(false)}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {filteredNavItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                  location.pathname.startsWith(item.path)
                    ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                }`}
                onClick={(e) => alNavegar(e, item)}
              >
                <i className={`${item.icon} text-lg`}></i>
                <span className="font-medium">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <Button
          icon="pi pi-sign-out"
          label="Cerrar Sesión"
          className="w-full p-button-outlined p-button-danger"
          onClick={handleLogout}
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header - Fixed */}
      <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <Button
              icon="pi pi-bars"
              className="p-button-text p-button-rounded"
              onClick={() => setSidebarVisible(true)}
              aria-label="Abrir menú"
            />
            <div className="flex items-center space-x-3">
              <i className={`${currentNavItem?.icon || 'pi pi-building'} text-xl text-blue-500`}></i>
              <h1 className="text-lg font-semibold text-gray-800">{pageTitle}</h1>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* El correo se oculta en telefono: a 390 px aprieta el titulo de
                la pagina hasta hacerlo ilegible. El boton de salir se queda. */}
            <div className="hidden sm:flex items-center space-x-2">
              <i className="pi pi-user text-gray-500"></i>
              <span className="text-sm text-gray-600">{user}</span>
            </div>
            <Button
              icon="pi pi-sign-out"
              className="p-button-text p-button-rounded p-button-danger"
              onClick={handleLogout}
              tooltip="Cerrar Sesión"
              aria-label="Cerrar sesión"
            />
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <Sidebar
        visible={sidebarVisible}
        position="left"
        onHide={() => setSidebarVisible(false)}
        className="w-80"
        showCloseIcon={false}
      >
        {sidebarContent}
      </Sidebar>

      {/* Main Content - Adjusted for fixed header */}
      {/* ⚠️ NUNCA un atajo `p-*` junto a `pt-20`. `sm:p-6` vive dentro de una
          media query, que en la hoja de Tailwind va DESPUÉS de las utilidades
          base: a partir de 640 px pisaba al `pt-20` con la misma especificidad
          y el espacio superior caía de 80 a 24 px. La cabecera fija mide 73 px,
          así que el tablero se metía DEBAJO de ella — medido: contenido en
          y=24 a 1440 y a 768 px, correcto sólo en móvil.
          Por eso se declaran los lados por separado y el superior nunca compite. */}
      <main className="pt-20 px-3 pb-3 sm:px-6 sm:pb-6">
        <div className="max-w mx-auto px-0 sm:px-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout; 